import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { Archive, Plus } from "lucide-react";
import Navbar from "@/components/Navbar";
import { useProperties } from "@/hooks/useProperties";
import { useCohostProperties } from "@/hooks/useCohostProperties";
import { useUserCapabilities } from "@/hooks/useUserCapabilities";
import { usePropertyStatusBadges } from "@/hooks/usePropertyStatusBadges";
import { useHostLabels } from "@/hooks/useHostLabels";
import { PropertyCard } from "@/components/owner/PropertyCard";
import { SharedPropertyCard } from "@/components/owner/SharedPropertyCard";
import AddPropertySheet from "@/components/owner/AddPropertySheet";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageContainer } from "@/components/layout/PageContainer";
import { WorkspaceChip } from "@/components/layout/WorkspaceChip";
import { useArchivedProperties } from "@/hooks/useArchivedProperties";
import { AppTour } from "@/components/tour/AppTour";
import { useAppTour } from "@/hooks/useAppTour";
import { showPropertiesWorkspaceChip } from "@/lib/workspaceMode";

/** Slice 2 P1/P2 + Slice 25 Yours / Shared with you. */
const PropertiesList = () => {
  const { is_owner, is_cohost, is_cleaner, has_cleaner_assignment, can_create_properties, isLoading: capsLoading } =
    useUserCapabilities();
  const { data: ownerProperties = [], isLoading: ownerLoading, error: ownerError } = useProperties();
  const { data: cohostProperties = [], isLoading: cohostLoading } = useCohostProperties();
  const { data: archivedProperties } = useArchivedProperties();
  const [addOpen, setAddOpen] = useState(false);
  const { activeTour, closeTour } = useAppTour("owner-dashboard");

  const ownerIds = new Set(ownerProperties.map((p) => p.id));
  const cohostOnly = cohostProperties.filter((p) => !ownerIds.has(p.id));
  const hasYours = ownerProperties.length > 0;
  const hasShared = cohostOnly.length > 0;
  const showSections = hasShared;
  const showChip = showPropertiesWorkspaceChip({ is_cleaner, is_owner, is_cohost, has_cleaner_assignment });
  const teamOnly = is_cohost && !is_owner;
  const ownerOnlyEmpty = !hasYours && !hasShared && !is_cleaner;
  const cleanerEmpty = !hasYours && !hasShared && is_cleaner;
  const headerAdd = can_create_properties && (hasYours || ownerOnlyEmpty);

  const yoursIds = ownerProperties.map((p) => p.id);
  const sharedIds = cohostOnly.map((p) => p.id);
  const shouldLoadStatusBadges = !ownerLoading && !cohostLoading && yoursIds.length > 0;
  const { data: statusMap } = usePropertyStatusBadges(yoursIds, shouldLoadStatusBadges);
  const { data: hostLabels = {} } = useHostLabels(sharedIds);
  const archivedCount = archivedProperties?.length || 0;
  const isLoading = ownerLoading || cohostLoading || capsLoading;
  const error = ownerError;

  const openAdd = () => {
    if (!can_create_properties) return;
    setAddOpen(true);
  };

  const yoursGrid = (
    <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 xl:grid-cols-3" data-tour="properties-list">
      {ownerProperties.map((property) => (
        <PropertyCard
          key={property.id}
          property={{
            id: property.id,
            name: property.name,
            address: property.address,
            unit: property.unit ?? null,
            property_photo_url: property.property_photo_url,
            hospitable_property_id: property.hospitable_property_id,
          }}
          status={statusMap?.[property.id]}
        />
      ))}
    </div>
  );

  const sharedGrid = (
    <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 xl:grid-cols-3">
      {cohostOnly.map((property) => (
        <SharedPropertyCard
          key={property.id}
          property={{
            id: property.id,
            name: property.name,
            address: property.address,
            unit: property.unit ?? null,
            property_photo_url: property.property_photo_url,
          }}
          hostLabel={hostLabels[property.id] ?? ""}
        />
      ))}
    </div>
  );

  const yoursEmptyDashed = (
    <div className="flex flex-col items-center gap-3 rounded-[18px] border-[1.5px] border-dashed border-[#d7e0e6] bg-white px-[18px] py-[22px] text-center">
      <div className="flex h-[58px] w-[58px] items-center justify-center rounded-[18px] bg-[#eef3f6]">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 11l8-6.5 8 6.5v8a1.5 1.5 0 01-1.5 1.5h-13A1.5 1.5 0 014 19z" />
        </svg>
      </div>
      <div className="text-[15px] font-medium leading-[1.45] text-[#64748b]">You don’t own any units yet.</div>
      {can_create_properties ? (
        <button
          type="button"
          onClick={openAdd}
          className="flex min-h-12 items-center rounded-[13px] bg-[#24a89a] px-5 text-[16px] font-bold text-white hover:bg-[#1f958a]"
        >
          Add your own
        </button>
      ) : null}
    </div>
  );

  return (
    <AppLayout>
      <Navbar />

      <div className="border-b-[1.5px] border-[#e6edf1] bg-[#fbfdfe]">
        <PageContainer className="py-5 sm:py-6">
          <div className="flex flex-col gap-3.5">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h1 className="text-[26px] font-bold tracking-[-0.5px] text-[#0f1720]">Properties</h1>
                {!showSections && !cleanerEmpty ? (
                  <p className="mt-1 text-[14px] font-medium text-[#64748b]">
                    {ownerProperties.length === 0
                      ? can_create_properties
                        ? "Add your first unit"
                        : "Properties shared with you"
                      : `${ownerProperties.length} unit${ownerProperties.length === 1 ? "" : "s"}`}
                  </p>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                {archivedCount > 0 && is_owner ? (
                  <Link to="/owner/properties/archived">
                    <Button variant="ghost" size="sm" className="gap-2 text-[#64748b]">
                      <Archive className="h-4 w-4" />
                      <span className="hidden sm:inline">Archived</span>
                      <span className="rounded bg-[#eef3f6] px-1.5 py-0.5 text-xs">{archivedCount}</span>
                    </Button>
                  </Link>
                ) : null}
                {headerAdd ? (
                  <>
                    <Button
                      onClick={openAdd}
                      data-tour="add-property-btn"
                      className="hidden h-[42px] rounded-xl bg-[#24a89a] px-[18px] text-[14px] font-bold text-white shadow-none hover:bg-[#1f958a] sm:inline-flex"
                    >
                      + Add property
                    </Button>
                    <Button
                      onClick={openAdd}
                      data-tour="add-property-btn-mobile"
                      className="inline-flex h-12 w-12 rounded-[14px] bg-[#24a89a] p-0 text-white shadow-none hover:bg-[#1f958a] sm:hidden"
                      aria-label="Add property"
                    >
                      <Plus className="h-5 w-5" strokeWidth={2.6} />
                    </Button>
                  </>
                ) : null}
              </div>
            </div>
            {showChip ? <WorkspaceChip active="properties" /> : null}
          </div>
        </PageContainer>
      </div>

      <PageContainer className="py-4 sm:py-6">
        {isLoading ? (
          <p className="text-[14px] font-medium text-[#64748b]">Loading…</p>
        ) : error ? (
          <div className="py-16 text-center">
            <p className="text-[16px] font-bold text-[#0f1720]">Unable to load</p>
            <p className="mt-1 text-[14px] text-[#64748b]">Refresh and try again.</p>
          </div>
        ) : cleanerEmpty ? (
          <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 px-8 pb-16 text-center" data-tour="properties-list">
            <div className="flex h-[78px] w-[78px] items-center justify-center rounded-[22px] bg-[#eef3f6]">
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 11l8-6.5 8 6.5v8a1.5 1.5 0 01-1.5 1.5h-13A1.5 1.5 0 014 19z" />
              </svg>
            </div>
            <div className="flex flex-col gap-[7px]">
              <div className="text-[27px] font-bold tracking-[-0.6px] text-[#0f1720]">No properties</div>
              <div className="text-[15px] font-medium leading-[1.45] text-[#64748b]">Units you add are yours to run.</div>
            </div>
            {can_create_properties ? (
              <Button
                onClick={openAdd}
                data-tour="add-property-btn"
                className="h-[58px] gap-2.5 rounded-[15px] bg-[#24a89a] px-7 text-[18px] font-bold text-white shadow-none hover:bg-[#1f958a]"
              >
                <Plus className="h-5 w-5" strokeWidth={2.6} />
                Add first property
              </Button>
            ) : null}
          </div>
        ) : showSections ? (
          <div className="flex flex-col gap-[18px]" data-tour="properties-list">
            {teamOnly && !hasYours ? null : (
            <div className="flex flex-col gap-2.5">
              <div className="flex items-baseline gap-3">
                <div className="text-[12px] font-bold tracking-[0.8px] text-[#8494a2]">YOURS</div>
                {hasYours ? (
                  <div className="text-[13px] font-medium text-[#94a3b8]">
                    {ownerProperties.length} unit{ownerProperties.length === 1 ? "" : "s"}
                  </div>
                ) : null}
              </div>
              {hasYours ? yoursGrid : yoursEmptyDashed}
            </div>
            )}
            <div className="flex flex-col gap-2.5">
              <div className="flex items-baseline gap-3">
                <div className="text-[12px] font-bold tracking-[0.8px] text-[#8494a2]">SHARED WITH YOU</div>
                <div className="text-[13px] font-medium text-[#94a3b8]">
                  {cohostOnly.length} unit{cohostOnly.length === 1 ? "" : "s"}
                </div>
              </div>
              {sharedGrid}
            </div>
          </div>
        ) : ownerProperties.length === 0 ? (
          <div className="flex min-h-[50vh] flex-col items-center justify-center gap-6 pb-16" data-tour="properties-list">
            <div className="flex h-[112px] w-[112px] items-center justify-center rounded-full bg-[#e7edf1]">
              <svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 11l8-6.5 8 6.5v8a1.5 1.5 0 01-1.5 1.5h-13A1.5 1.5 0 014 19z" />
              </svg>
            </div>
            <div className="text-[30px] font-bold tracking-[-0.6px] text-[#0f1720]">No properties</div>
            {can_create_properties ? (
              <Button
                onClick={openAdd}
                data-tour="add-property-btn"
                className="h-[58px] gap-2.5 rounded-[15px] bg-[#24a89a] px-7 text-[18px] font-bold text-white shadow-none hover:bg-[#1f958a]"
              >
                <Plus className="h-5 w-5" strokeWidth={2.6} />
                Add first property
              </Button>
            ) : (
              <p className="max-w-sm text-center text-[14px] font-medium text-[#64748b]">
                Ask the account owner to share a property with you.
              </p>
            )}
          </div>
        ) : (
          yoursGrid
        )}
      </PageContainer>

      {can_create_properties ? (
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent className="gap-0 overflow-hidden rounded-[20px] border-[1.5px] border-[#d7e0e6] p-0 sm:max-w-[480px] [&>button]:hidden">
            <AddPropertySheet onClose={() => setAddOpen(false)} />
          </DialogContent>
        </Dialog>
      ) : null}

      <AppTour tour={activeTour} onClose={closeTour} />
    </AppLayout>
  );
};

export default PropertiesList;
